export const name = "SUM";
export const summary = `Gets the sum of non-\`NULL\` values.`;
export const reference = `
## SUM

Gets the sum of non-\`NULL\` values.

\`\`\`
SUM(
  [DISTINCT]
  expression
)
[OVER over_clause]

over_clause:
  { named_window | ( [window_specification] ) }

window_specification:
  [named_window]
  [PARTITION BY partition_expression [, ...] ]
  [ORDER BY expression [ { ASC | DESC }] [, ...] ]
  [window_frame_clause]
\`\`\`

### Description

Returns the sum of non-\`NULL\` values in an aggregated group.

To learn more about the optional aggregate clauses that you can pass into this function, see Aggregate function calls.

This function can be used with the \`AGGREGATION_THRESHOLD\` clause.

To learn more about the \`OVER\` clause and how to use it, see Window function calls.

\`SUM\` can be used with differential privacy. For more information, see Differentially private aggregate functions.

Caveats:

- If the aggregated group is empty or the argument is \`NULL\` for all rows in the group, returns \`NULL\`.
- If the argument is \`NaN\` for any row in the group, returns \`NaN\`.
- If the argument is \`[+|-]Infinity\` for any row in the group, returns either\`[+|-]Infinity\` or \`NaN\`.
- If there is numeric overflow, produces an error.
- If a floating-point type is returned, the result is non-deterministic, which means you might receive a different result each time you use this function.


### Supported Argument Types

- Any supported numeric data type
- \`INTERVAL\`


### Return Data Types

| INPUT | \`INT64\` | \`NUMERIC\` | \`BIGNUMERIC\` | \`FLOAT64\` | \`INTERVAL\` |
| --- | --- | --- | --- | --- | --- |
| OUTPUT | \`INT64\` | \`NUMERIC\` | \`BIGNUMERIC\` | \`FLOAT64\` | \`INTERVAL\` |


### Examples



\`\`\`
SELECT SUM(x) AS sum
FROM UNNEST([1, 2, 3, 4, 5, 4, 3, 2, 1]) AS x;

/*-----*
 | sum |
 +-----+
 | 25 |
 *-----*/
\`\`\`



\`\`\`
SELECT SUM(DISTINCT x) AS sum
FROM UNNEST([1, 2, 3, 4, 5, 4, 3, 2, 1]) AS x;

/*-----*
 | sum |
 +-----+
 | 15 |
 *-----*/
\`\`\`



\`\`\`
SELECT
  x,
  SUM(x) OVER (PARTITION BY MOD(x, 3)) AS sum
FROM UNNEST([1, 2, 3, 4, 5, 4, 3, 2, 1]) AS x;

/*---+-----*
 | x | sum |
 +---+-----+
 | 3 | 6 |
 | 3 | 6 |
 | 1 | 10 |
 | 4 | 10 |
 | 4 | 10 |
 | 1 | 10 |
 | 2 | 9 |
 | 5 | 9 |
 | 2 | 9 |
 *---+-----*/
\`\`\`



\`\`\`
SELECT
  x,
  SUM(DISTINCT x) OVER (PARTITION BY MOD(x, 3)) AS sum
FROM UNNEST([1, 2, 3, 4, 5, 4, 3, 2, 1]) AS x;

/*---+-----*
 | x | sum |
 +---+-----+
 | 3 | 3 |
 | 3 | 3 |
 | 1 | 5 |
 | 4 | 5 |
 | 4 | 5 |
 | 1 | 5 |
 | 2 | 7 |
 | 5 | 7 |
 | 2 | 7 |
 *---+-----*/
\`\`\`



\`\`\`
SELECT SUM(x) AS sum
FROM UNNEST([]) AS x;

/*------*
 | sum |
 +------+
 | NULL |
 *------*/
\`\`\`




source: https://cloud.google.com/bigquery/docs/reference/standard-sql/aggregate_functions
`;
